import { buildDecorArt } from './art/decor';
import {
  grubSprite,
  grubSquashed,
  nipperSprite,
  shellSprite,
  snugSprite,
} from './art/enemies';
import { HERO_POSES, type HeroForm, heroSprite } from './art/hero';
import {
  bannerSprite,
  emberSeedSprite,
  heartPodSprite,
  orbSprite,
  pennantSprite,
  prismSprite,
  sunberrySprite,
  sunbitSprite,
} from './art/items';
import { buildTileArt } from './art/tiles';
import type { ThemeName } from './palette';
import { PixelBuf } from './pixel';

/** Baked sprite bank with lazy mirrored and tinted variants. */
export class Sprites {
  private map = new Map<string, PixelBuf>();
  private flips = new Map<string, PixelBuf>();
  private tints = new Map<string, PixelBuf>();

  add(name: string, buf: PixelBuf): void {
    this.map.set(name, buf);
  }

  has(name: string): boolean {
    return this.map.has(name);
  }

  get(name: string): PixelBuf {
    const b = this.map.get(name);
    if (!b) throw new Error(`missing sprite: ${name}`);
    return b;
  }

  flip(name: string): PixelBuf {
    let f = this.flips.get(name);
    if (!f) {
      f = this.get(name).flipped();
      this.flips.set(name, f);
    }
    return f;
  }

  /** Mirrored or normal depending on facing. */
  facing(name: string, faceRight: boolean): PixelBuf {
    return faceRight ? this.get(name) : this.flip(name);
  }

  tint(name: string, color: string, faceRight = true): PixelBuf {
    const key = `${name}|${color}|${faceRight ? 'r' : 'l'}`;
    let t = this.tints.get(key);
    if (!t) {
      t = (faceRight ? this.get(name) : this.flip(name)).tinted(color, 1);
      this.tints.set(key, t);
    }
    return t;
  }
}

const THEME_LIST: ThemeName[] = ['day', 'dusk', 'cave'];

export function buildSprites(): Sprites {
  const s = new Sprites();

  const forms: HeroForm[] = ['small', 'big', 'fire'];
  for (const form of forms) {
    for (const pose of HERO_POSES) {
      s.add(`hero.${form}.${pose}`, heroSprite(form, pose));
    }
  }

  s.add('grub.0', grubSprite(0));
  s.add('grub.1', grubSprite(1));
  s.add('grub.squash', grubSquashed());
  s.add('snug.0', snugSprite(0));
  s.add('snug.1', snugSprite(1));
  s.add('shell.0', shellSprite(0));
  s.add('shell.1', shellSprite(1));
  s.add('shell.2', shellSprite(2));
  s.add('nipper.0', nipperSprite(false));
  s.add('nipper.1', nipperSprite(true));

  for (let i = 0; i < 4; i++) s.add(`coin.${i}`, sunbitSprite(i));
  s.add('berry', sunberrySprite());
  s.add('ember.0', emberSeedSprite(0));
  s.add('ember.1', emberSeedSprite(1));
  s.add('prism.0', prismSprite(0));
  s.add('prism.1', prismSprite(1));
  s.add('pod', heartPodSprite());
  for (let i = 0; i < 4; i++) s.add(`orb.${i}`, orbSprite(i));
  s.add('banner', bannerSprite());
  s.add('pennant.0', pennantSprite(false));
  s.add('pennant.1', pennantSprite(true));

  for (const theme of THEME_LIST) {
    const art = buildTileArt(theme);
    for (const [k, buf] of Object.entries(art)) s.add(`tile.${theme}.${k}`, buf);
  }

  const decor = buildDecorArt();
  for (const [k, buf] of Object.entries(decor)) s.add(`decor.${k}`, buf);

  return s;
}
